/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faXmark,
  faPaperPlane,
} from '@fortawesome/pro-regular-svg-icons';
import { ArbeitszeitArbeitszeitantragStatus } from '@/gql/graphql';
import { StatusText } from './StatusText';
import { AntragInfoText } from './AntragInfoText';
import { Antrag } from '@/domain/arbeitszeiten/entities';
import { renderAvatar } from './renderAvatar';
import { useProfile } from '@/hooks/useProfile';

type AntragVerlaufProps = {
  antraege: Antrag[];
  currentAntrag: Antrag;
  beginnAZ?: Date;
  endeAZ?: Date;
  initialsVorgesetzter: string;
};

export function AntragVerlauf({
  antraege,
  currentAntrag,
  beginnAZ,
  endeAZ,
}: AntragVerlaufProps) {
  const { manager } = useProfile();

  const initialsVorgesetzterVorname = manager?.vorname[0].toUpperCase() ?? '';
  const initialsVorgesetzterNachname = manager?.nachname[0].toUpperCase() ?? '';

  const sortedAntraege = [...antraege].sort((a, b) => {
    const dateA = a.erstellungsdatum?.getTime() ?? 0;
    const dateB = b.erstellungsdatum?.getTime() ?? 0;
    return dateB - dateA;
  });

  const renderVerticalLine = () => (
    <div className="flex items-center justify-center ps-[13px]">
      <div className="w-px h-18 bg-neutrals-500" />
    </div>
  );

  return (
    <div>
      <div className="mx-8 mb-4">
        {antraege.length > 0 && (
          <div>
            {currentAntrag.offen && (
              <hr className="border-[0.5px] border-neutrals-200 my-8" />
            )}
            <div className="text-neutrals-700 font-bold text-sm">Verlauf</div>
          </div>
        )}
      </div>

      <div>
        {sortedAntraege?.map((antrag, index) => (
          <div
            key={antrag.id}
            className="flex flex-col items-start rounded-xs px-6 m-[7px] w-[98%]">
            {index > 0 && renderVerticalLine()}
            <div className="flex items-center w-full gap-2 h-[22px] mb-[0.2rem]">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center ${
                  antrag.status === ArbeitszeitArbeitszeitantragStatus.Genehmigt
                    ? 'bg-primary-green-100 text-primary-green-600'
                    : 'bg-neutrals-200'
                }`}>
                <FontAwesomeIcon
                  icon={
                    antrag.status ===
                    ArbeitszeitArbeitszeitantragStatus.Genehmigt
                      ? faCheck
                      : faXmark
                  }
                  color="text-neutrals-900"
                  className="text-[0.75rem]"
                />
              </div>
              <div
                className={`flex-1 ${
                  antrag.status ===
                    ArbeitszeitArbeitszeitantragStatus.Genehmigt &&
                  antrag.bemerkungVorgesetzter
                    ? 'mt-12'
                    : antrag.status ===
                        ArbeitszeitArbeitszeitantragStatus.Genehmigt
                      ? 'mt-4'
                      : 'mt-12'
                }`}>
                <StatusText
                  status={antrag.status}
                  aenderungsdatum={
                    antrag.aenderungsdatum
                      ? new Date(antrag.aenderungsdatum)
                      : undefined
                  }
                  beginn={
                    antrag.beginn?.time
                      ? {
                          time: new Date(antrag.beginn.time),
                          isChangeRequested: antrag.beginn.isChangeRequested,
                        }
                      : { time: beginnAZ, isChangeRequested: false }
                  }
                  ende={
                    antrag.ende?.time
                      ? {
                          time: new Date(antrag.ende.time),
                          isChangeRequested: antrag.ende.isChangeRequested,
                        }
                      : { time: endeAZ, isChangeRequested: false }
                  }
                  mitarbeiter={antrag.mitarbeiter}
                />
                {antrag.bemerkungVorgesetzter && (
                  <>
                    <div className="flex items-center gap-1">
                      {renderAvatar(
                        null,
                        initialsVorgesetzterVorname,
                        initialsVorgesetzterNachname,
                      )}
                      <div className="text-sm">
                        {antrag.bemerkungVorgesetzter}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            {renderVerticalLine()}
            <div className="flex items-center w-full gap-2 h-2.5 pb-1">
              <div className="w-7 h-7 rounded-full bg-neutrals-200 flex items-center justify-center">
                <FontAwesomeIcon
                  icon={faPaperPlane}
                  className="text-[0.75rem] text-black"
                />
              </div>

              <div
                className={`flex-1 ${
                  antrag.bemerkungAntragsteller ? 'mt-12' : 'mt-0'
                }`}>
                <AntragInfoText
                  beginn={antrag.beginn}
                  ende={antrag.ende}
                  beginnAZ={
                    beginnAZ
                      ? { time: beginnAZ, isChangeRequested: false }
                      : undefined
                  }
                  endeAZ={
                    endeAZ
                      ? { time: endeAZ, isChangeRequested: false }
                      : undefined
                  }
                  aenderungsdatum={antrag.aenderungsdatum}
                  erstellungsdatum={antrag.erstellungsdatum}
                  offen={antrag.offen}
                  mitarbeiter={antrag.mitarbeiter}
                />
                {antrag.bemerkungAntragsteller && (
                  <>
                    <div className="flex items-center ">
                      {renderAvatar(
                        currentAntrag.mitarbeiter.bildUrl ?? null,
                        antrag.mitarbeiter.vorname,
                        antrag.mitarbeiter.nachname,
                      )}
                      <div className="text-sm">
                        {antrag.bemerkungAntragsteller}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
